import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Globe, Loader2, Sparkles, XCircle } from 'lucide-react';
import { InputField } from './InputField';
import { supabase } from '../../lib/supabase';

interface WebsiteEnrichFieldProps {
  value: string;
  onChange: (value: string) => void;
  onEnrich: (data: WebsiteEnrichResult) => void;
  /** true = input nudo per le righe di proprietà del drawer (niente label/box). */
  bare?: boolean;
}

export interface WebsiteEnrichResult {
  email?: string;
  phone?: string;
  pec?: string;
  vat_number?: string;
  description?: string;
  social_links?: Record<string, string>;
}

type EnrichStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

export function WebsiteEnrichField({ value, onChange, onEnrich, bare = false }: WebsiteEnrichFieldProps) {
  const [status, setStatus] = useState<EnrichStatus>('idle');
  const [enrichData, setEnrichData] = useState<WebsiteEnrichResult | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastCheckedRef = useRef<string>('');

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const scrapeWebsite = useCallback(async (url: string) => {
    const normalized = url.trim().toLowerCase();
    if (normalized === lastCheckedRef.current) return;
    lastCheckedRef.current = normalized;

    setStatus('loading');
    setShowDropdown(true);

    try {
      // We call the edge function with a dummy client_id to just get the scrape
      // Actually, we need to scrape directly from the client side
      // Use a lightweight approach: fetch the website and parse basic info
      let fetchUrl = url.trim();
      if (!fetchUrl.startsWith('http://') && !fetchUrl.startsWith('https://')) {
        fetchUrl = 'https://' + fetchUrl;
      }

      // Use a CORS proxy or direct fetch (may fail due to CORS)
      // Best approach: call our edge function for scraping
      const { data, error } = await supabase.functions.invoke('lead-enrich-preview', {
        body: { website: fetchUrl },
      });

      const wd = data?.website;
      if (error || !wd) {
        setStatus('not_found');
        return;
      }

      if (!wd.email && !wd.phone && !wd.pec && !wd.vat_number && !wd.description) {
        setStatus('not_found');
        return;
      }

      setEnrichData(wd);
      setStatus('found');
    } catch {
      setStatus('error');
    }
  }, []);

  const handleChange = useCallback((val: string) => {
    onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Detect if it looks like a URL/domain
    const clean = val.trim();
    const looksLikeUrl = clean.length > 4 && (
      clean.includes('.') && /\.[a-z]{2,}$/i.test(clean)
    );

    if (looksLikeUrl) {
      debounceRef.current = setTimeout(() => scrapeWebsite(val), 1500);
    } else {
      setStatus('idle');
      setShowDropdown(false);
      lastCheckedRef.current = '';
    }
  }, [onChange, scrapeWebsite]);

  const handleApply = useCallback(() => {
    if (enrichData) {
      onEnrich(enrichData);
      setShowDropdown(false);
    }
  }, [enrichData, onEnrich]);

  return (
    <div className="relative" ref={dropdownRef}>
      {bare ? (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Vuoto"
            aria-label="Sito web"
            className="w-full min-w-0 flex-1 bg-transparent text-[13px] text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.4)] dark:placeholder-gray-500 focus:outline-none"
          />
          {status === 'loading' && <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-blue-500" />}
          {status === 'found' && !showDropdown && <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />}
        </div>
      ) : (
        <>
          <InputField
            label="Sito Web"
            value={value}
            onChange={handleChange}
            icon={Globe}
            placeholder="www.esempio.com"
            hint={status === 'idle' ? 'Inserisci il dominio per recuperare automaticamente i dati' : ''}
          />

          {status === 'loading' && (
            <div className="absolute right-3 top-[38px] flex items-center">
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
            </div>
          )}
        </>
      )}

      {showDropdown && status !== 'idle' && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          {status === 'loading' && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Analisi del sito in corso...
            </div>
          )}

          {status === 'not_found' && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <XCircle className="h-4 w-4" />
              Nessun dato trovato dal sito web
            </div>
          )}

          {status === 'error' && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <XCircle className="h-4 w-4" />
              Impossibile analizzare il sito web
            </div>
          )}

          {status === 'found' && enrichData && (
            <button
              type="button"
              onClick={handleApply}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Dati trovati dal sito web
                  </span>
                  <div className="space-y-0.5">
                    {enrichData.email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">Email: {enrichData.email}</p>
                    )}
                    {enrichData.phone && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">Tel: {enrichData.phone}</p>
                    )}
                    {enrichData.pec && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">PEC: {enrichData.pec}</p>
                    )}
                    {enrichData.vat_number && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">P.IVA: {enrichData.vat_number}</p>
                    )}
                    {enrichData.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {enrichData.description}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Clicca per compilare automaticamente i campi vuoti
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
